/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { formatErrors } from 'utils/functions';
import { useLocation } from 'react-router-dom';
import PopupCreateRename from '../components/Popups/PopupCreateRename';
import PopupUploadFile from '../components/Popups/PopupUploadFile';
import PopupInfoFile from '../components/Popups/PopupInfoFile';
import PopupEditFile from '../components/Popups/PopupEditFile';
import PopupViewsFile from '../components/Popups/PopupViewsFile';
// import PopupMove from './Popups/PopupMove';
import PopupFileVersions from '../components/Popups/PopupFileVersions';
import { addBookmark, deleteBookmark, getFolderContents } from '../modules/actions';
import useHashHandler from './useHashHandler';
import useBreadcrumbHandler from './useBreadcrumbHandler';

export const DocumentsContext = createContext();

function DocumentsProvider({ hash = false, children }) {
  const literals = useSelector(({ i18n }) => i18n.literals.dataRoom);
  const literalsCommon = useSelector(({ i18n }) => i18n.literals.common);
  const startup = useSelector(({ global }) => global.startup);

  const [unauthorized, setUnauthorized] = useState(false);
  const location = useLocation();

  // const canEdit = editable && (actualFolder?.permission?.level >= PERMISSION_LEVELS.EDIT);
  const PAGE_SIZE = 15;

  const [loading, setLoading] = useState(true);

  const [subfolders, setSubfolders] = useState([]);
  const [content, setContent] = useState([]);

  const [showCreateOrRename, setShowCreateOrRename] = useState(null);
  const [showUploadPopup, setShowUploadPopup] = useState(null);
  const [showInfoPopup, setShowInfoPopup] = useState(null);
  const [showEditPopup, setShowEditPopup] = useState(null);
  const [showViewersPopup, setShowViewersPopup] = useState(null);
  const [showVersionsPopup, setShowVersionsPopup] = useState(null);
  // const [showMovePopup, setShowMovePopup] = useState(null);

  const rolePath = location.pathname.split('/').filter(p => p !== 'embed')[1];

  const [actualFolder, setActualFolder] = useHashHandler(hash, startup);
  const breadcrumb = useBreadcrumbHandler(actualFolder, startup);

  const fetchFolderContent = async (auxSearch = '') => {
    if (!loading) {
      setLoading(true);
      setUnauthorized(false);
    }
    try {
      let auxSubfolders;
      if (actualFolder.id !== 'recent') {
        auxSubfolders = await getFolderContents(rolePath, startup.id, actualFolder.id, {
          page: 0, size: 0, sort: '-created_at', search: auxSearch, filters: { 'element.type': 'folder' },
        });
      }
      const auxContent = await getFolderContents(rolePath, startup.id, actualFolder.id, {
        page: 0, size: PAGE_SIZE, sort: '-created_at', search: auxSearch, filters: { 'element.type': ['document', 'link'] },
      });

      if ((actualFolder.id === 'recent' || auxSubfolders) && auxContent) {
        setSubfolders(auxSubfolders?.items || []);
        setContent(auxContent);
        setLoading(false);
      }
    } catch (e) {
      const error = formatErrors(e);
      if (error?.unauthorized) {
        setUnauthorized(true);
      }
      setLoading(false);
    }
  };

  const handleBookmark = async (item) => {
    const setBookmark = (type, bookmark, del = false) => {
      if (type === 'folder') {
        setSubfolders(prev => (del && !bookmark
          ? prev.filter(fold => (fold.id !== item.id))
          : prev.map(fold => (fold.id === item.id ? { ...fold, _bookmark: bookmark } : fold))));
      } else {
        setContent(prev => ({
          items: (del && !bookmark
            ? prev.items.filter(cont => (cont.id !== item.id))
            : prev.items.map(cont => (cont.id === item.id ? { ...cont, _bookmark: bookmark } : cont))),
          total: prev.total,
        }));
      }
    };
    const { id, _type: type, _bookmark: bookmark } = item;
    const element = { id, type };
    setBookmark(type, 'loading');

    try {
      if (bookmark) {
        await deleteBookmark(rolePath, startup.id, element);
      } else {
        await addBookmark(rolePath, startup.id, element);
      }
      setBookmark(type, !bookmark, actualFolder.id === 'bookmarks');
    } catch {
      setBookmark(type, bookmark);
    }
  };

  useEffect(() => {
    if (actualFolder) {
      setSubfolders([]);
      setContent([]);
      fetchFolderContent();
    }
  }, [actualFolder?.id]);

  const providerValues = useMemo(() => ({
    literals,
    literalsCommon,
    startup,
    loading,
    unauthorized,
    breadcrumb,

    actualFolder,
    setActualFolder,
    subfolders,
    content,

    handleBookmark,
    setShowCreateOrRename,
    setShowUploadPopup,
    setShowInfoPopup,
    setShowEditPopup,
    setShowViewersPopup,
    setShowVersionsPopup,
  }), [literals, literalsCommon, startup, loading, unauthorized, actualFolder, subfolders, content]);

  return (
    <DocumentsContext.Provider value={providerValues}>
      {children}
      {
        showCreateOrRename && (
          <PopupCreateRename
            folder={showCreateOrRename}
            literals={literals}
            startup={startup}
            rolePath={rolePath}
            parent={actualFolder}
            setSubfolders={setSubfolders}
            onClose={() => setShowCreateOrRename(null)}
          />
        )
      }
      {
        showUploadPopup && (
          <PopupUploadFile
            onClose={() => setShowUploadPopup(null)}
            literals={literals}
            startup={startup}
            parent={actualFolder}
            setContent={setContent}
            setFolders={setSubfolders}
          />
        )
      }
      {
        showInfoPopup && (
          <PopupInfoFile
            literals={literals}
            literalsCommon={literalsCommon}
            onClose={() => setShowInfoPopup(null)}
            file={showInfoPopup}
            setContent={setContent}
            handleBookmark={handleBookmark}
          />
        )
      }
      {
        showEditPopup && (
          <PopupEditFile
            literals={literals}
            literalsCommon={literalsCommon}
            startup={startup}
            folder={actualFolder}
            onClose={() => setShowEditPopup(null)}
            content={showEditPopup}
            setContent={setContent}
          />
        )
      }
      {showViewersPopup && (
        <PopupViewsFile
          literals={literals}
          file={showViewersPopup}
          onClose={() => setShowViewersPopup(null)}
        />
      )}
      {/* {showMovePopup && (
        <PopupMove
          literals={literals}
          elemToMove={showMovePopup}
          onClose={() => setShowMovePopup(false)}
          actualFolder={actualFolder}
          setSubfolders={setSubfolders}
          setContent={setContent}
        />
      )} */}
      {showVersionsPopup && (
        <PopupFileVersions
          literals={literals}
          literalsCommon={literalsCommon}
          onClose={() => setShowVersionsPopup(null)}
          content={showVersionsPopup}
          setContent={setContent}
        />
      )}
    </DocumentsContext.Provider>
  );
}

DocumentsProvider.propTypes = {
  hash: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export default DocumentsProvider;
